<template>
  <svg
    class="svg-icon"
    xmlns="http://www.w3.org/2000/svg"
    :class="svgClassName"
    :viewBox="viewBox"
    :fill="color"
    :width="width"
    :height="computedHeight"
    :aria-labelledby="titleId"
    :style="{ transform: `rotate(${rotate}deg)` }"
    :focusable="focusable ? 'true' : 'false'"
    :role="role"
  >
    <title :id="titleId">{{ title }} icon</title>
    <desc v-if="description" :id="descriptionId">{{ description }}</desc>
    <component :is="dynamicComponent" :key="name" @view-box="setViewBox" />
  </svg>
</template>

<script setup lang="ts">
  import { ref, computed, defineAsyncComponent } from 'vue'

  interface Props {
    name: string
    width?: number | string
    color?: string
    rotate?: number | string
    description?: string
    focusable?: boolean
    role?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    width: 30,
    color: 'currentColor',
    rotate: 0,
    description: '',
    focusable: false,
    role: 'img',
  })

  interface Emits {
    error: [error: Error]
    loaded: [data: { width: number; height: number; viewBox: string }]
  }

  const emit = defineEmits<Emits>()

  const height = ref(0)
  const viewBox = ref('0 0 0 0')

  const computedWidth = computed(() => Number(props.width))
  const computedHeight = computed(() => height.value || computedWidth.value)

  const svgClassName = computed(() => {
    const hyphenateName = props.name.replace(/([A-Z])/g, '-$1')
    const downcaseName = hyphenateName.toLowerCase()
    return `svg${downcaseName}`
  })

  const title = computed(() => {
    return props.name.replace(/([A-Z])/g, ' $1').trim()
  })

  const titleId = computed(() => `svg-title-${props.name.toLowerCase()}`)
  const descriptionId = computed(() => `svg-desc-${props.name.toLowerCase()}`)

  const dynamicComponent = defineAsyncComponent({
    loader: () => import(`@/components/svgs/${props.name}.vue`),
    errorComponent: () => null,
    delay: 100,
    timeout: 15000,
    onError: error => {
      emit('error', error)
      console.warn(`Failed to load SVG component: ${props.name}`, error)
    },
    loadingComponent: () => null,
  })

  const setViewBox = (newViewBox: string) => {
    try {
      const viewBoxValues = newViewBox.split(' ')
      if (viewBoxValues.length === 4) {
        const [, , viewBoxWidth, viewBoxHeight] = viewBoxValues.map(Number)

        if (
          !isNaN(viewBoxWidth) &&
          !isNaN(viewBoxHeight) &&
          viewBoxWidth > 0 &&
          viewBoxHeight > 0
        ) {
          const computedHeight =
            (computedWidth.value / viewBoxWidth) * viewBoxHeight
          height.value = computedHeight
          viewBox.value = newViewBox
          emit('loaded', {
            width: computedWidth.value,
            height: computedHeight,
            viewBox: newViewBox,
          })
        }
      }
    } catch (error) {
      emit('error', error as Error)
    }
  }
</script>

<style scoped>
  .svg-icon {
    display: inline-block;
    line-height: 0;
    max-width: 100%;
    vertical-align: baseline;
    pointer-events: none;
    transition: all 0.2s ease;
  }

  .svg-icon:focus-visible {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    .svg-icon {
      transition: none;
    }
  }
</style>
